/**
 * Catálogo canónico de features de Atreus.
 *
 * Fuente única de verdad sobre qué features existen, cuáles son **core**
 * (siempre ON, no tocables desde admin) y cuáles son **opcionales**
 * (togglables por tenant desde la tabla `tenant_features`).
 *
 * Política de evaluación (ver `isFeatureEnabled`):
 *  - Core → siempre true.
 *  - Opcional → true si existe fila `tenant_features` para el tenant con
 *    `enabled = true`. Fila ausente se trata como disabled (fail-closed).
 *
 * El frontend debe ocultar UI de features disabled; el backend debe
 * rechazar mutaciones que las requieran (defensa en profundidad — la UI
 * esconde pero un cliente tRPC rebelde podría intentarlo).
 *
 * Fase 5.5 (admin web): el dueño del software provisiona tenants y elige
 * qué features opcionales activar. Los presets por vertical
 * (`restobarPreset`, `retailPreset`…) viven aquí para que el admin los
 * aplique en un click.
 */
export const features = {
    // -------------------- Core --------------------
    orders: {
        id: 'orders',
        name: 'Pedidos',
        description: 'Crear pedidos con origen mostrador, agregar items, cobrar.',
        kind: 'core',
        defaultEnabled: true,
    },
    products: {
        id: 'products',
        name: 'Productos',
        description: 'Catálogo de productos del menú / inventario.',
        kind: 'core',
        defaultEnabled: true,
    },
    categories: {
        id: 'categories',
        name: 'Categorías',
        description: 'Clasificación de productos para navegación.',
        kind: 'core',
        defaultEnabled: true,
    },
    cash: {
        id: 'cash',
        name: 'Caja',
        description: 'Apertura/cierre de caja, movimientos, arqueo.',
        kind: 'core',
        defaultEnabled: true,
    },
    payments: {
        id: 'payments',
        name: 'Pagos',
        description: 'Registro de pagos (efectivo, tarjeta, transferencia).',
        kind: 'core',
        defaultEnabled: true,
    },
    // -------------------- Opcionales --------------------
    tables: {
        id: 'tables',
        name: 'Mesas',
        description: 'Pedidos con origen "mesa" y número de mesa.',
        kind: 'optional',
        defaultEnabled: true,
    },
    delivery: {
        id: 'delivery',
        name: 'Domicilios',
        description: 'Pedidos con origen "domicilio" y dirección del cliente.',
        kind: 'optional',
        defaultEnabled: true,
    },
    kitchen_flow: {
        id: 'kitchen_flow',
        name: 'Flujo de cocina',
        description: 'Estados intermedios preparando/listo/entregado. Si está off, los pedidos van directo de confirmado a pagado.',
        kind: 'optional',
        defaultEnabled: true,
    },
    tips: {
        id: 'tips',
        name: 'Propinas',
        description: 'Campo de propina editable en el carrito.',
        kind: 'optional',
        defaultEnabled: true,
    },
    inventory: {
        id: 'inventory',
        name: 'Inventario',
        description: 'Trackea stock por producto. Muestra cantidad / bajo stock / agotado y permite ajustes. Útil para retail (ferretería, tienda). Productos sin esta feature se comportan como "servicio" (stock ilimitado, como platos de restobar).',
        kind: 'optional',
        defaultEnabled: false,
    },
    stats: {
        id: 'stats',
        name: 'Estadísticas',
        description: 'Dashboard de ventas detalladas: KPIs, ventas por hora, top productos, desglose por método de pago y por vendedor. Requiere datos históricos — tiene más valor a partir del primer mes de uso.',
        kind: 'optional',
        defaultEnabled: false,
    },
    expenses: {
        id: 'expenses',
        name: 'Gastos',
        description: 'Registro de insumos comprados, gastos operativos (alquiler, servicios, salarios) e inversiones. Si está activo + caja abierta y el pago es en efectivo, descuenta automáticamente de la caja del usuario que registra.',
        kind: 'optional',
        defaultEnabled: false,
    },
    customer_credit: {
        id: 'customer_credit',
        name: 'Ventas a crédito / cartera',
        description: 'Registro de clientes, ventas a crédito (parte o todo el pedido queda como saldo pendiente del cliente) y abonos posteriores a la cartera. Los abonos entran a la caja del turno que los recibe.',
        kind: 'optional',
        defaultEnabled: false,
    },
    chatbot: {
        id: 'chatbot',
        name: 'Chatbot WhatsApp',
        description: 'Bot conversacional integrado a WhatsApp Business: atiende pedidos y consultas 24/7 con el catálogo, zonas y conocimiento del tenant. La operación del bot vive en un Worker separado (chatbot-saas); Atreus solo guarda el puntero (slug + número) y el estado live/paused. Onboarding inicial por CLI del proyecto chatbot.',
        kind: 'optional',
        defaultEnabled: false,
    },
};
export const featureIds = Object.keys(features);
export const optionalFeatureIds = Object.values(features)
    .filter((f) => f.kind === 'optional')
    .map((f) => f.id);
export const coreFeatureIds = Object.values(features)
    .filter((f) => f.kind === 'core')
    .map((f) => f.id);
export const isFeatureId = (id) => Object.prototype.hasOwnProperty.call(features, id);
/**
 * Evalúa si una feature está habilitada para un conjunto de rows
 * `tenant_features` dado. Core features siempre devuelven true (aunque
 * no exista fila). Opcional: `enabled === true`; fila ausente = false.
 */
export const isFeatureEnabled = (id, tenantFeatures) => {
    // Unknown ids (typos, stale deploys) fail closed. Catalog is source of truth.
    if (!isFeatureId(id))
        return false;
    const def = features[id];
    if (def.kind === 'core')
        return true;
    const row = tenantFeatures.find((r) => r.featureId === id);
    return row?.enabled === true;
};
// -------------------- Presets por vertical --------------------
/**
 * Lista de features opcionales a activar al provisionar un tenant según el
 * vertical. El admin de Fase 5.5 elegirá un preset (o custom) al crear el
 * tenant, y esto se traduce en filas `tenant_features` con `enabled = true`.
 *
 * Un preset omite intencionalmente features core — éstas nunca van a
 * `tenant_features`.
 */
export const presets = {
    /** Restobar, bar, cafetería. El vertical del piloto. */
    restobar: [
        'tables',
        'delivery',
        'kitchen_flow',
        'tips',
        'stats',
        'expenses',
        'customer_credit',
    ],
    /** Juguería, heladería. Sin mesas ni cocina (mostrador y domicilio). */
    juice_bar: ['delivery', 'tips', 'stats', 'expenses'],
    /** Ferretería, retail simple. Solo mostrador, sin propinas ni cocina. El
     *  inventario viene activado porque este tipo de tenant vende bienes
     *  físicos donde el stock importa (a diferencia de un restobar). */
    retail: ['inventory', 'expenses'],
    /** Comida rápida con domicilio + cocina pero sin mesas. */
    quick_service: ['delivery', 'kitchen_flow', 'expenses'],
};
export const listPresets = () => Object.keys(presets);
/** Devuelve los ids opcionales a activar por defecto para el preset dado. */
export const featuresForPreset = (preset) => presets[preset];
// -------------------- Config schemas por feature --------------------
export * from './configs/index.js';
//# sourceMappingURL=index.js.map